/**
 * useInvestigationStore - Hook for investigation management with immediate API sync.
 *
 * Provides CRUD operations for investigations and graph operations.
 * Graph data: trackers are nodes, edges link trackers together.
 */

import { create } from "zustand";
import { createResourceHook } from "../use-resource-store";
import {
  useInvestigations,
  useCreateInvestigation,
  useUpdateInvestigation,
  useDeleteInvestigation,
} from "@/api/investigation";
import {
  Investigation,
  InvestigationCreate,
  InvestigationGraph,
  TrackerNode,
  InvestigationEdge,
} from "@/models/investigation";

/**
 * Hook for investigation list management with immediate API sync.
 */
export const useInvestigationsStore = createResourceHook<
  Investigation,
  InvestigationCreate
>({
  resourceName: "investigation",
  resourcePath: "investigations",

  useQueryHook: () => {
    const { data, isLoading, error, refetch } = useInvestigations();
    return {
      data: data ?? undefined,
      isLoading,
      error: error as Error | null,
      refetch,
    };
  },

  useCreateMutation: () => {
    const mutation = useCreateInvestigation();
    return {
      mutateAsync: async (data: InvestigationCreate) => {
        const result = await mutation.mutateAsync(data);
        return result as unknown as Investigation;
      },
    };
  },

  useUpdateMutation: () => {
    const mutation = useUpdateInvestigation();
    return {
      mutateAsync: async (
        data: { uriParams: { id: string } } & Record<string, unknown>
      ) => {
        const result = await mutation.mutateAsync(
          data as Parameters<typeof mutation.mutateAsync>[0]
        );
        return result as unknown as Investigation;
      },
    };
  },

  useDeleteMutation: () => {
    const mutation = useDeleteInvestigation();
    return {
      mutateAsync: async (data: { uriParams: { id: string } }) => {
        await mutation.mutateAsync(data);
      },
    };
  },
});

/**
 * Local state for the currently active investigation graph.
 * Used for optimistic updates and local UI state management.
 */
interface InvestigationGraphState {
  /** Currently selected investigation ID */
  selectedInvestigationId: string | null;

  /** Local graph state (for optimistic updates) */
  graph: InvestigationGraph | null;

  /** Selected tracker ID for context menu/actions */
  selectedTrackerId: string | null;

  /** Whether the add tracker modal is open */
  isAddTrackerModalOpen: boolean;

  /** Source tracker ID when linking a new tracker */
  linkingFromTrackerId: string | null;

  /** Tracker ID for documents panel (null = panel closed) */
  documentsPanelTrackerId: string | null;

  /** Actions */
  setSelectedInvestigation: (id: string | null) => void;
  setGraph: (graph: InvestigationGraph | null) => void;
  setSelectedTracker: (trackerId: string | null) => void;
  openAddTrackerModal: (linkingFromTrackerId?: string | null) => void;
  closeAddTrackerModal: () => void;
  openDocumentsPanel: (trackerId: string) => void;
  closeDocumentsPanel: () => void;

  /** Optimistic updates for trackers */
  addTrackerOptimistic: (tracker: TrackerNode) => void;
  updateTrackerPositionOptimistic: (
    trackerId: string,
    position: { x: number; y: number }
  ) => void;
  removeTrackerOptimistic: (trackerId: string) => void;

  /** Optimistic updates for edges */
  addEdgeOptimistic: (edge: InvestigationEdge) => void;
  removeEdgeOptimistic: (edgeId: string) => void;

  /** Reset state */
  reset: () => void;
}

const initialState = {
  selectedInvestigationId: null,
  graph: null,
  selectedTrackerId: null,
  isAddTrackerModalOpen: false,
  linkingFromTrackerId: null,
  documentsPanelTrackerId: null,
};

export const useInvestigationGraphStore = create<InvestigationGraphState>(
  (set) => ({
    ...initialState,

    setSelectedInvestigation: (id) => set({ selectedInvestigationId: id }),

    setGraph: (graph) => set({ graph }),

    setSelectedTracker: (trackerId) => set({ selectedTrackerId: trackerId }),

    openAddTrackerModal: (linkingFromTrackerId = null) =>
      set({ isAddTrackerModalOpen: true, linkingFromTrackerId }),

    closeAddTrackerModal: () =>
      set({ isAddTrackerModalOpen: false, linkingFromTrackerId: null }),

    openDocumentsPanel: (trackerId) => set({ documentsPanelTrackerId: trackerId }),

    closeDocumentsPanel: () => set({ documentsPanelTrackerId: null }),

    addTrackerOptimistic: (tracker) =>
      set((state) => {
        if (!state.graph) return state;
        return {
          graph: {
            ...state.graph,
            trackers: [...state.graph.trackers, tracker],
          },
        };
      }),

    updateTrackerPositionOptimistic: (trackerId, position) =>
      set((state) => {
        if (!state.graph) return state;
        return {
          graph: {
            ...state.graph,
            trackers: state.graph.trackers.map((t) =>
              t.id === trackerId ? { ...t, position } : t
            ),
          },
        };
      }),

    removeTrackerOptimistic: (trackerId) =>
      set((state) => {
        if (!state.graph) return state;

        return {
          graph: {
            trackers: state.graph.trackers.filter((t) => t.id !== trackerId),
            // Also remove edges connected to this tracker
            edges: state.graph.edges.filter(
              (e) => e.source !== trackerId && e.target !== trackerId
            ),
          },
        };
      }),

    addEdgeOptimistic: (edge) =>
      set((state) => {
        if (!state.graph) return state;
        return {
          graph: {
            ...state.graph,
            edges: [...state.graph.edges, edge],
          },
        };
      }),

    removeEdgeOptimistic: (edgeId) =>
      set((state) => {
        if (!state.graph) return state;
        return {
          graph: {
            ...state.graph,
            edges: state.graph.edges.filter((e) => e.id !== edgeId),
          },
        };
      }),

    reset: () => set(initialState),
  })
);

export default useInvestigationsStore;
